import { pushEvent } from '@/js/lib/analytics/data-layer';

/**
 * Query params allowed to reach GA4. Everything else is dropped, because
 * /auth/reset-password carries `token` (a raw password-reset secret) and `email` — see
 * AppUserResetPasswordNotification::resetUrl and ResetPasswordPage.vue. Neither may ever
 * leave the browser through analytics.
 */
const ALLOWED_QUERY_PARAMS = new Set(['page', 'search', 'sort', 'tab']);

/**
 * Kept in `page_location` only. GA4 reads campaign attribution off it, and overriding it without
 * these collapses every paid, email and referral visit into direct / none. They stay out of
 * `page_path` so the Pages report is not split per campaign.
 */
const ATTRIBUTION_QUERY_PARAMS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
]);

export const PAGE_VIEW_EVENT = 'virtual_page_view';

function toUrl(value: string): URL | null {
  try {
    return new URL(value, window.location.origin);
  }
  catch {
    return null;
  }
}

function filterPath(value: string, allowed: ReadonlySet<string>): string {
  const url = toUrl(value);

  if (url === null) {
    return '';
  }

  const safe = new URLSearchParams();

  for (const [key, param] of url.searchParams) {
    if (allowed.has(key)) {
      safe.append(key, param);
    }
  }

  const query = safe.toString();

  return query === '' ? url.pathname : `${url.pathname}?${query}`;
}

/** Returns a same-origin path with only whitelisted query params kept. */
export function sanitizePath(value: string): string {
  return filterPath(value, ALLOWED_QUERY_PARAMS);
}

/** Same, plus the campaign parameters GA4 needs to attribute the session. */
function sanitizeLocationPath(value: string): string {
  return filterPath(
    value,
    new Set([...ALLOWED_QUERY_PARAMS, ...ATTRIBUTION_QUERY_PARAMS]),
  );
}

/**
 * External referrers are passed through — they are standard GA4 attribution data and none of
 * our secrets live there. Same-origin referrers go through the whitelist.
 */
function sanitizeReferrer(value: string): string {
  if (value === '') {
    return '';
  }

  const url = toUrl(value);

  if (url === null) {
    return '';
  }

  if (url.origin !== window.location.origin) {
    return value;
  }

  return `${window.location.origin}${sanitizePath(value)}`;
}

export type PageViewOptions = {
  path: string
  referrer: string
  routeName?: string
};

export function trackPageView({ path, referrer, routeName }: PageViewOptions): void {
  pushEvent(PAGE_VIEW_EVENT, {
    page_path: sanitizePath(path),
    page_location: `${window.location.origin}${sanitizeLocationPath(path)}`,
    page_referrer: sanitizeReferrer(referrer),
    page_title: document.title,
    route_name: routeName ?? '',
  });
}

/** Page view for a regular server-rendered document. */
export function trackInitialPageView(): void {
  trackPageView({
    path: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer,
  });
}
